/* ==========================================================================
   FILE: global/components/Slider.jsx
   IAMONEAI - Slider/Range Component
   Range input with labels, marks, and personality axis support
   ========================================================================== */

import React, { forwardRef, useId, useState, useCallback } from 'react';
import './Slider.css';

// ==========================================================================
// SLIDER COMPONENT
// ==========================================================================

/**
 * Slider/Range input component
 * 
 * @param {Object} props
 * @param {number} value - Current value
 * @param {Function} onChange - Change handler
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {number} step - Step increment
 * @param {string} label - Slider label
 * @param {boolean} showValue - Show current value
 * @param {string} minLabel - Label for minimum end
 * @param {string} maxLabel - Label for maximum end
 * @param {Array} marks - Array of { value, label } for tick marks
 * 
 * @example
 * <Slider
 *   label="Volume"
 *   value={volume}
 *   onChange={(e) => setVolume(e.target.value)}
 *   min={0}
 *   max={100}
 *   showValue
 * />
 */
const Slider = forwardRef(({
  value = 0,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  label,
  showValue = false,
  valueFormatter = (v) => v,
  minLabel,
  maxLabel,
  marks = [],
  disabled = false,
  size = 'md',
  color = 'primary',
  className = '',
  id: providedId,
  name,
  ...props
}, ref) => {
  const generatedId = useId();
  const id = providedId || generatedId;

  // Calculate fill percentage
  const percentage = ((value - min) / (max - min)) * 100;

  const wrapperClasses = [
    'slider-wrapper',
    disabled && 'slider-disabled',
    className,
  ].filter(Boolean).join(' ');

  const sliderClasses = [
    'slider',
    `slider-${size}`,
    `slider-${color}`,
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {/* Header with label and value */}
      {(label || showValue) && (
        <div className="slider-header">
          {label && (
            <label htmlFor={id} className="slider-label">
              {label}
            </label>
          )}
          {showValue && (
            <span className="slider-value">
              {valueFormatter(value)}
            </span>
          )}
        </div>
      )}

      {/* Slider Track */}
      <div className="slider-container">
        <input
          ref={ref}
          type="range"
          id={id}
          name={name}
          className={sliderClasses}
          value={value}
          onChange={onChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          style={{ '--slider-fill': `${percentage}%` }}
          {...props}
        />

        {/* Marks */}
        {marks.length > 0 && (
          <div className="slider-marks">
            {marks.map((mark) => {
              const markPercentage = ((mark.value - min) / (max - min)) * 100;
              return (
                <div
                  key={mark.value}
                  className={[
                    'slider-mark',
                    value >= mark.value && 'slider-mark-active',
                  ].filter(Boolean).join(' ')}
                  style={{ left: `${markPercentage}%` }}
                >
                  <span className="slider-mark-dot" />
                  {mark.label && (
                    <span className="slider-mark-label">{mark.label}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Min/Max Labels */}
      {(minLabel || maxLabel) && (
        <div className="slider-range-labels">
          <span className="slider-min-label">{minLabel}</span>
          <span className="slider-max-label">{maxLabel}</span>
        </div>
      )}
    </div>
  );
});

Slider.displayName = 'Slider';

// ==========================================================================
// PERSONALITY AXIS SLIDER
// ==========================================================================

/**
 * Special slider for IAMONEAI personality axis (0-1 scale)
 * Shows opposing traits at each end
 * 
 * @example
 * <PersonalitySlider
 *   axis="energy"
 *   value={personality.energy}
 *   onChange={(value) => updatePersonality('energy', value)}
 *   lowLabel="Calm"
 *   highLabel="Energetic"
 * />
 */
export function PersonalitySlider({
  axis,
  value = 0.5,
  onChange,
  lowLabel = 'Low',
  highLabel = 'High',
  description,
  disabled = false,
  className = '',
}) {
  const handleChange = (e) => {
    const newValue = parseFloat(e.target.value);
    onChange?.(newValue);
  };

  // Format percentage display
  const percentage = Math.round(value * 100);

  const wrapperClasses = [
    'personality-slider',
    disabled && 'personality-slider-disabled',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {/* Axis Name */}
      <div className="personality-slider-header">
        <span className="personality-slider-axis">{axis}</span>
        <span className="personality-slider-value">{percentage}%</span>
      </div>

      {/* Description */}
      {description && (
        <p className="personality-slider-description">{description}</p>
      )}

      {/* Slider */}
      <div className="personality-slider-track-container">
        <input
          type="range"
          className="personality-slider-input"
          value={value}
          onChange={handleChange}
          min={0}
          max={1}
          step={0.05}
          disabled={disabled}
          style={{ '--slider-fill': `${percentage}%` }}
          aria-label={`${axis} level`}
        />
      </div>

      {/* Trait Labels */}
      <div className="personality-slider-labels">
        <span className="personality-slider-low">{lowLabel}</span>
        <span className="personality-slider-high">{highLabel}</span>
      </div>
    </div>
  );
}

// ==========================================================================
// RANGE SLIDER (Two Thumbs)
// ==========================================================================

/**
 * Range slider with two thumbs for selecting a range
 * 
 * @example
 * <RangeSlider
 *   label="Price Range"
 *   minValue={minPrice}
 *   maxValue={maxPrice}
 *   onChange={({ min, max }) => setPriceRange(min, max)}
 *   min={0}
 *   max={1000}
 *   formatValue={(v) => `$${v}`}
 * />
 */
export function RangeSlider({
  minValue = 0,
  maxValue = 100,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  label,
  formatValue = (v) => v,
  disabled = false,
  className = '',
}) {
  const [localMin, setLocalMin] = useState(minValue);
  const [localMax, setLocalMax] = useState(maxValue);

  const handleMinChange = useCallback((e) => {
    const value = Math.min(Number(e.target.value), localMax - step);
    setLocalMin(value);
    onChange?.({ min: value, max: localMax });
  }, [localMax, step, onChange]);

  const handleMaxChange = useCallback((e) => {
    const value = Math.max(Number(e.target.value), localMin + step);
    setLocalMax(value);
    onChange?.({ min: localMin, max: value });
  }, [localMin, step, onChange]);

  const minPercentage = ((localMin - min) / (max - min)) * 100;
  const maxPercentage = ((localMax - min) / (max - min)) * 100;

  const wrapperClasses = [
    'range-slider-wrapper',
    disabled && 'range-slider-disabled',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {/* Header */}
      {label && (
        <div className="range-slider-header">
          <span className="range-slider-label">{label}</span>
          <span className="range-slider-values">
            {formatValue(localMin)} – {formatValue(localMax)}
          </span>
        </div>
      )}

      {/* Track Container */}
      <div className="range-slider-container">
        {/* Track Background */}
        <div className="range-slider-track" />
        
        {/* Active Track */}
        <div
          className="range-slider-active-track"
          style={{
            left: `${minPercentage}%`,
            width: `${maxPercentage - minPercentage}%`,
          }}
        />

        {/* Min Input */}
        <input
          type="range"
          className="range-slider-input range-slider-input-min"
          value={localMin}
          onChange={handleMinChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-label="Minimum value"
        />

        {/* Max Input */}
        <input
          type="range"
          className="range-slider-input range-slider-input-max"
          value={localMax}
          onChange={handleMaxChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-label="Maximum value"
        />
      </div>

      {/* Min/Max Labels */}
      <div className="range-slider-bounds">
        <span>{formatValue(min)}</span>
        <span>{formatValue(max)}</span>
      </div>
    </div>
  );
}

// ==========================================================================
// STEPPED SLIDER (Discrete Steps)
// ==========================================================================

/**
 * Slider with discrete labeled steps
 * 
 * @example
 * <SteppedSlider
 *   label="Response Length"
 *   value={responseLength}
 *   onChange={setResponseLength}
 *   steps={[
 *     { value: 'brief', label: 'Brief' },
 *     { value: 'concise', label: 'Concise' },
 *     { value: 'balanced', label: 'Balanced' },
 *     { value: 'detailed', label: 'Detailed' },
 *   ]}
 * />
 */
export function SteppedSlider({
  value,
  onChange,
  steps = [],
  label,
  disabled = false,
  className = '',
}) {
  const currentIndex = steps.findIndex(s => s.value === value);
  const percentage = steps.length > 1 
    ? (currentIndex / (steps.length - 1)) * 100 
    : 0;

  const handleChange = (e) => {
    const index = parseInt(e.target.value, 10);
    if (steps[index]) {
      onChange?.(steps[index].value);
    }
  };

  const wrapperClasses = [
    'stepped-slider-wrapper',
    disabled && 'stepped-slider-disabled',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {/* Label */}
      {label && (
        <div className="stepped-slider-header">
          <span className="stepped-slider-label">{label}</span>
          <span className="stepped-slider-value">
            {steps[currentIndex]?.label || ''}
          </span>
        </div>
      )}

      {/* Track Container */}
      <div className="stepped-slider-container">
        <input
          type="range"
          className="stepped-slider-input"
          value={currentIndex >= 0 ? currentIndex : 0}
          onChange={handleChange}
          min={0}
          max={steps.length - 1}
          step={1}
          disabled={disabled}
          style={{ '--slider-fill': `${percentage}%` }}
          aria-label={label}
        />

        {/* Step Dots */}
        <div className="stepped-slider-dots">
          {steps.map((step, index) => {
            const dotPercentage = steps.length > 1 
              ? (index / (steps.length - 1)) * 100 
              : 0;
            return (
              <div
                key={step.value}
                className={[
                  'stepped-slider-dot',
                  currentIndex >= index && 'stepped-slider-dot-active',
                ].filter(Boolean).join(' ')}
                style={{ left: `${dotPercentage}%` }}
              />
            );
          })}
        </div>
      </div>

      {/* Step Labels */}
      <div className="stepped-slider-labels">
        {steps.map((step, index) => {
          const labelPercentage = steps.length > 1 
            ? (index / (steps.length - 1)) * 100 
            : 0;
          return (
            <span
              key={step.value}
              className={[
                'stepped-slider-step-label',
                currentIndex === index && 'stepped-slider-step-label-active',
              ].filter(Boolean).join(' ')}
              style={{ left: `${labelPercentage}%` }}
            >
              {step.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default Slider;